import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth';
import { toast } from '@/components/ui/sonner';

export const useKeyboardShortcuts = () => {
  const navigate = useNavigate();
  const { hasAccess } = useSupabaseAuth();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only trigger if no input field is focused
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || 
                           activeElement?.tagName === 'TEXTAREA' || 
                           activeElement?.hasAttribute('contenteditable');

      if (isInputFocused) return;

      const { key, ctrlKey, metaKey, altKey, shiftKey } = event;
      const isModifierPressed = ctrlKey || metaKey;

      // Prevent browser shortcuts when our shortcuts are triggered
      const preventDefault = () => {
        event.preventDefault();
        event.stopPropagation();
      };

      // Navigation shortcuts
      switch (key.toLowerCase()) {
        case 'h':
          if (isModifierPressed) {
            preventDefault();
            navigate('/');
            toast.success('Navigated to Home');
          }
          break;

        case 'd':
          if (isModifierPressed) {
            preventDefault();
            navigate('/dashboard');
            toast.success('Navigated to Dashboard');
          }
          break;

        case 'c':
          if (isModifierPressed && !shiftKey) {
            preventDefault();
            navigate('/courses');
            toast.success('Navigated to All Courses');
          }
          break;

        case 'a':
          if (isModifierPressed && !shiftKey) {
            preventDefault();
            navigate('/assistant');
            toast.success('Navigated to Assistant');
          }
          break;

        case 'n':
          if (isModifierPressed) {
            preventDefault();
            navigate('/notifications');
            toast.success('Navigated to Notifications');
          }
          break;

        case 'p':
          if (isModifierPressed && !shiftKey) {
            preventDefault();
            navigate('/profile');
            toast.success('Navigated to Profile');
          }
          break;

        case 's':
          if (isModifierPressed && shiftKey) {
            preventDefault();
            navigate('/settings');
            toast.success('Navigated to Settings');
          }
          break;

        case 'm':
          if (isModifierPressed && hasAccess('admin')) {
            preventDefault();
            navigate('/admin');
            toast.success('Navigated to Admin Panel');
          }
          break;

        case 'f':
          if (isModifierPressed && hasAccess('staff')) {
            preventDefault();
            navigate('/staff');
            toast.success('Navigated to Staff Portal');
          }
          break;

        case 'arrowleft':
          if (altKey) {
            preventDefault();
            window.history.back();
            toast.success('Navigated Back');
          }
          break;

        case 'arrowright':
          if (altKey) {
            preventDefault();
            window.history.forward();
            toast.success('Navigated Forward');
          }
          break;

        case '?':
          if (!isModifierPressed) {
            preventDefault();
            showShortcutsHelp();
          }
          break;

        case 'escape':
          // Close any open modals/dialogs
          const openDialog = document.querySelector('[data-state="open"]');
          if (openDialog) {
            const closeButton = openDialog.querySelector('[data-dismiss]') as HTMLElement;
            closeButton?.click();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, hasAccess]);

  const showShortcutsHelp = () => {
    const shortcuts = [
      'Ctrl/Cmd + H: Home',
      'Ctrl/Cmd + D: Dashboard', 
      'Ctrl/Cmd + C: All Courses',
      'Ctrl/Cmd + A: Assistant',
      'Ctrl/Cmd + N: Notifications',
      'Ctrl/Cmd + P: Profile',
      'Ctrl/Cmd + Shift + S: Settings',
      'Ctrl/Cmd + K: Search',
      'Alt + ←/→: Back/Forward',
      '?: Show this help',
      'Esc: Close dialogs'
    ];

    toast.info(
      <div>
        <div className="font-semibold mb-2">Keyboard Shortcuts</div>
        <div className="text-sm space-y-1">
          {shortcuts.map((shortcut, index) => (
            <div key={index} className="font-mono">{shortcut}</div>
          ))}
        </div>
      </div>,
      { duration: 10000 }
    );
  };

  return { showShortcutsHelp };
};

// Component to enable keyboard shortcuts
const KeyboardShortcuts: React.FC = () => {
  useKeyboardShortcuts();
  return null;
};

export default KeyboardShortcuts;